/*
 * <fleet-overview> — the default view (the welcome page). Uses the SHARED base-ui
 * home-hero (`.home-page`/`.home-hero`/`.home-greeting`/`.home-prompt-bar`/
 * `.prompt-chip`) + `.entity-tab-header` so it is pixel-identical to the gateway
 * app's home: blue-gradient greeting, the prompt bar with voice button, pink-sparkle
 * AI hint chips, and the pill tabs in the same left position/size. fleet-specific:
 * the tabs GROUP the gateway cards (Status / Region / Channel) into sections.
 *
 * Light DOM so the shared base-ui SCSS styles everything (no hand-rolled welcome).
 */
import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { pillTabBar, type PillTab } from '@vitesse/base-ui';
import { fleetGatewayCard } from '../components/fleet-gateway-card';
import { groupFleet, type GroupMode } from '../core/fleet-model';
import { ft, LocaleController } from '../core/i18n';
import type { FleetGateway } from '../core/fleet-client';

@customElement('fleet-overview')
export class FleetOverview extends LitElement {
  @property({ attribute: false }) gateways: FleetGateway[] = [];
  @property() userName = '';
  /** orgId → human org name; used for the Organization grouping. */
  @property({ attribute: false }) orgNames: Map<string, string> = new Map();

  @state() private groupMode: GroupMode = 'status';

  constructor() {
    super();
    new LocaleController(this); // re-render greeting/tabs/hints on language switch
  }

  /** Grouping pill tabs — locale-aware (built per render, not a module const).
   *  Icons are vitesse-icon glyph names (raw inline lucide SVG, no lucide-react),
   *  same as the app frontend's pill bars. */
  private get groupTabs(): PillTab[] {
    return [
      { id: 'status', label: ft('group.status'), icon: 'activity' },
      { id: 'region', label: ft('group.region'), icon: 'globe' },
      { id: 'channel', label: ft('group.channel'), icon: 'waypoints' },
      { id: 'organization', label: ft('group.organization'), icon: 'building-2' },
    ];
  }

  /** The four AI prompt-chip suggestions, localized. */
  private get hints(): string[] {
    return [ft('prompt.q1'), ft('prompt.q2'), ft('prompt.q3'), ft('prompt.q4')];
  }

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  private askAi(prompt: string): void {
    this.dispatchEvent(
      new CustomEvent('vitesse-ai-prompt', { detail: { prompt }, bubbles: true, composed: true }),
    );
  }

  private openGateway(g: FleetGateway): void {
    this.dispatchEvent(
      new CustomEvent('gateway-open', {
        detail: { fingerprint: g.deviceFingerprint },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render(): TemplateResult {
    const groups = groupFleet(this.gateways, this.groupMode, this.orgNames);
    return html`
      <div class="home-page">
        <div class="home-hero">
          <div class="home-greeting-row">
            <div class="home-greeting-text">
              <h1 class="home-greeting">${ft('home.greeting', { name: this.userName || ft('home.there') })}</h1>
              <p class="home-subtitle">${ft('home.subtitle')}</p>
            </div>
          </div>

          <button class="home-prompt-bar" @click=${() => this.askAi('')}>
            <vitesse-icon name="sparkles" size="18" class="home-prompt-icon"></vitesse-icon>
            <span class="home-prompt-text">${ft('home.askAnything')}</span>
            <span
              class="home-prompt-voice-button"
              role="button"
              tabindex="0"
              title=${ft('home.voice')}
              @click=${(e: Event) => e.stopPropagation()}
            >
              <vitesse-icon name="audio-lines" size="18"></vitesse-icon>
            </span>
          </button>

          <div class="prompt-chips home-prompt-chips-centered">
            ${this.hints.map(
              (h) => html`<button class="prompt-chip" @click=${() => this.askAi(h)}>
                <vitesse-icon name="sparkles" size="12" class="prompt-chip-sparkle"></vitesse-icon>
                <span class="prompt-chip-text">${h}</span>
              </button>`,
            )}
          </div>
        </div>

        <div class="home-entity-section">
          <div class="entity-tab-header">
            ${pillTabBar({
              tabs: this.groupTabs,
              active: this.groupMode,
              onChange: (id) => (this.groupMode = id as GroupMode),
            })}
          </div>

          ${this.gateways.length === 0
            ? html`<div class="home-empty-gateways">
                <span class="home-empty-hint">${ft('home.empty')}</span>
              </div>`
            : html`<div class="base-grid">
                ${groups
                  .flatMap((grp) => grp.items)
                  .map((g) => fleetGatewayCard(g, (gw) => this.openGateway(gw), this.orgNames))}
              </div>`}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fleet-overview': FleetOverview;
  }
}
